import {
  IBranchDto,
  IBranchUpdateModel,
  IDeactivateBranchingResultDto,
  IDeleteBranchResultDto,
  IMergeRequestDto,
  IMergeResultDto
} from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import BranchingService from "../../../services/branching-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface BranchingProviderState {
  currentBranch: IBranchDto | undefined;
  branches: IBranchDto[];
  getBranches: () => Promise<IBranchDto[]>;
  createBranch: (branch: Pick<IBranchDto, "name" | "parentBranchId">) => Promise<IBranchDto>;
  setCurrentBranch: (branchId: string) => Promise<void>;
  renameBranch: (branchId: string, newName: string) => Promise<IBranchDto>;
  mergeBranch: (mergeData: IMergeRequestDto) => Promise<IMergeResultDto>;
  resolveMergeConflicts: (mergeData: IMergeRequestDto) => Promise<IMergeResultDto>;
  cancelMerge: (mergeResult?: IMergeResultDto) => Promise<boolean>;
  deactivateBranching: () => Promise<IDeactivateBranchingResultDto>;
  deleteBranch: (branchId: string) => Promise<IDeleteBranchResultDto>;
}

export class BranchingProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  BranchingProviderState
> {
  static defaultState: BranchingProviderState = {
    currentBranch: undefined,
    branches: [],
    getBranches: () => Promise.resolve([]),
    createBranch: () => Promise.resolve({} as IBranchDto),
    setCurrentBranch: () => Promise.resolve(),
    renameBranch: () => Promise.resolve({} as IBranchDto),
    mergeBranch: () => Promise.resolve({} as IMergeResultDto),
    resolveMergeConflicts: () => Promise.resolve({} as IMergeResultDto),
    cancelMerge: () => Promise.resolve(false),
    deactivateBranching: () => Promise.resolve({} as IDeactivateBranchingResultDto),
    deleteBranch: () => Promise.resolve({} as IDeleteBranchResultDto)
  };

  getProviderValues = (): BranchingProviderState => {
    return {
      currentBranch: this.state.currentBranch,
      branches: this.state.branches,
      getBranches: this.getBranches,
      createBranch: this.createBranch,
      setCurrentBranch: this.setCurrentBranch,
      renameBranch: this.renameBranch,
      mergeBranch: this.mergeBranch,
      resolveMergeConflicts: this.resolveMergeConflicts,
      cancelMerge: this.cancelMerge,
      deactivateBranching: this.deactivateBranching,
      deleteBranch: this.deleteBranch
    };
  };

  // Put the whole designer into view-only mode when the user is on a protected branch they can't
  // commit to (reuses the Viewer-role gating via KMainFunctions). No-op for committable branches.
  private syncBranchReadOnly = (branch: IBranchDto | undefined): void => {
    const readOnly: boolean = !!branch?.isProtected && branch?.permissions?.canMergeInto === false;
    KMainFunctions.setBranchReadOnly(readOnly);
  };

  getBranches = async () => {
    // Safety gating: avoid calling /branching/getall when branching is not enabled for this project.
    const activeRes = await BranchingService.IsBranchingActive();
    if (!activeRes.data) {
      KMainFunctions.setBranchReadOnly(false);
      this.setState({ currentBranch: undefined, branches: [] });
      return [];
    }

    return BranchingService.GetAllBranches().then((branches) => {
      const currentBranch = branches.data.find((branch) => branch.isCheckedOut === true);
      this.syncBranchReadOnly(currentBranch);
      this.setState({ currentBranch, branches: branches.data });
      return branches.data;
    });
  };

  createBranch = (branch: Pick<IBranchDto, "name" | "parentBranchId">) => {
    return BranchingService.CreateBranch(branch).then((branch) => {
      this.setState({ branches: [...this.state.branches, branch.data] });
      return branch.data;
    });
  };

  setCurrentBranch = async (branchId: string) => {
    // Set view-only state BEFORE reloading the project so the reload renders with the right gating.
    const branch = this.state.branches.find((b) => b.id === branchId);
    this.syncBranchReadOnly(branch);
    await this.partial.projectProvider.openProject(true);
    this.setState({ currentBranch: branch });

    // Force reload action definitions — openProject skips this when they're already loaded
    this.partial.projectProvider.loadActionDefinitions();
  };

  renameBranch = (branchId: string, newName: string) => {
    const branchInfo: IBranchUpdateModel = {
      branchId,
      newName
    };
    return BranchingService.RenameBranch(branchInfo).then((branch) => {
      return branch.data;
    });
  };

  mergeBranch = (mergeData: IMergeRequestDto) => {
    return BranchingService.MergeBranch(mergeData).then((conflicts) => {
      return conflicts.data;
    });
  };

  resolveMergeConflicts = (mergeData: IMergeRequestDto) => {
    return BranchingService.ResolveMergeConflicts(mergeData).then((conflicts) => {
      return conflicts.data;
    });
  };

  cancelMerge = (mergeResult?: IMergeResultDto) => {
    if (!mergeResult) {
      mergeResult = {} as IMergeResultDto;
    }
    return BranchingService.CancelMerge(mergeResult).then((result) => {
      return result.data;
    });
  };

  deactivateBranching = () => {
    return BranchingService.DeactivateBranching().then((result) => {
      this.setState({ currentBranch: undefined, branches: [] });
      return result.data;
    });
  };

  deleteBranch = (branchId: string) => {
    return BranchingService.DeleteBranch(branchId).then((result) => {
      const updatedBranches = this.state.branches.filter((b) => b.id !== branchId);
      const currentBranch =
        this.state.currentBranch?.id === branchId
          ? updatedBranches.find((b) => b.name?.toLowerCase() === "master")
          : this.state.currentBranch;
      this.setState({ branches: updatedBranches, currentBranch });
      return result.data;
    });
  };
}
